<template>
  <div class="ai-chat-bot">
    <button class="ai-chat-bot-btn" @click="openChatbot">LMD AI</button>
    <div class="ai-chat-bot-in">
      <div class="ai-chat-bot-top">
        <div class="ai-chat-bot-top-left">
          <img src="@/assets/images/logo.svg" alt="AI Chat Bot" />
          <h3>LMD AI</h3>
        </div>
        <div class="ai-chat-bot-top-right">
          <button class="chat-bat-minimize-btn"></button>
          <button class="chat-bat-zoom-btn"></button>
        </div>
      </div>
      <div class="ai-chat-bot-btm">
        <div
          class="ai-chat-bot-message-list-box"
          ref="scrollWrapper"
          data-simplebar
        >
          <div class="ai-chat-bot-message-list-box-in">
            <div
              v-for="message in messages"
              :key="message.id"
              class="bot-message-box"
              :class="
                message.is_user
                  ? 'ai-bot user_message_box'
                  : 'ai_bot_message_box'
              "
            >
              <div class="bot-message-box-in">
                <div v-if="message.file" class="message-file-preview">
                  <img
                    v-if="message.file.type.startsWith('image/')"
                    :src="message.file.url"
                    :alt="message.file.name"
                    class="uploaded-image-preview"
                  />
                  <div
                    v-else-if="message.file.type === 'application/pdf'"
                    class="uploaded-pdf-preview"
                  >
                    <div class="pdf-icon">📄</div>
                    <div class="pdf-info">
                      <p class="pdf-filename">{{ message.file.name }}</p>
                    </div>
                  </div>
                </div>
                <div
                  class="ai-chat-bot-message-show"
                  v-html="formatMessage(message.message)"
                ></div>
                <div class="bot-message-box-action">
                  <ul>
                    <li>{{ formatTime(message.created_at) }}</li>
                  </ul>
                </div>
              </div>
            
            </div>
            <div v-if="isLoading" class="bot-is-typing">
            Generating<span>...</span>
          </div>
          </div>
         
        </div>
        <div class="ai-chat-bot-message-write-box">
          <div class="ai-chat-bot-message-write-box-top">
            <textarea
              v-model="userInput"
              class="ai-chat-bot-message-write-box-field"
              placeholder="Type your message here..."
              @keyup.enter="sendMessage()"
            ></textarea>
            <button class="send-message-btn" @click="sendMessage()"></button>
          </div>
          <div class="ai-chat-bot-message-write-box-btm">
            <ul>
              <li><button class="ai-chat-bot-log-btn"></button></li>
              <li>
                <input
                  type="file"
                  ref="fileInput"
                  @change="handleFileUpload"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style="display: none"
                />
                <button
                  class="ai-chat-bot-attach-btn"
                  @click="triggerFileUpload"
                  v-if="isFileUploadEnabled"
                ></button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, computed } from "vue";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { API_BASE_URL } from "@/config/api.config";
import moment from "moment";
import SimpleBar from "simplebar";
import { Modal } from "bootstrap";

interface Message {
  id: number;
  is_user: boolean;
  message: string;
  created_at?: string;
  updated_at?: string;
  file?: {
    name: string;
    type: string;
    url: string;
  };
}

export default defineComponent({
  name: "ChatbotPopup",
  data() {
    return {
      messages: [] as Message[],
      userInput: "",
      sessionId: null as string | null,
      isLoading: false,
      simpleBarInstance: null as any,
      selectedFile: null as File | null,
    };
  },
  mounted() {
    const el = this.$refs.scrollWrapper as HTMLElement;
    if (el) {
      this.simpleBarInstance = new SimpleBar(el);
    }
    this.initializeSession();
    window.addEventListener('chatbot:refresh', this.fetchHistory as any);
  },
  beforeUnmount() {
    window.removeEventListener('chatbot:refresh', this.fetchHistory as any);
  },
  computed: {
    isFileUploadEnabled(): boolean {
      const lastMessage = this.messages[this.messages.length - 1];
      if (lastMessage && !lastMessage.is_user) {
        const messageText = lastMessage.message.toLowerCase();
        return (
          messageText.includes("resume") || messageText.includes("picture") || messageText.includes("image") || messageText.includes("photo") || messageText.includes("profile")
        );
      }
      return false;
    },
  },
  methods: {
    async openChatbot() {
      this.initializeSession();
      await this.fetchHistory();
      if (this.messages.length === 0) {
        await this.sendMessage("Initialize chatbot");
      }
    },
    initializeSession() {
      let sessionId = sessionStorage.getItem("chat_session_id");
      if (!sessionId) {
        sessionId = uuidv4();
        sessionStorage.setItem("chat_session_id", sessionId);
      }
      this.sessionId = sessionId;
    },
    async fetchHistory() {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/chat-history/?session_id=${this.sessionId}`
        );
        this.messages = response.data;
        
        const storageKey = `file_previews_${this.sessionId}`;
        const storedPreviews = sessionStorage.getItem(storageKey);
        if (storedPreviews) {
          const previewsMap = JSON.parse(storedPreviews);
          const updatedPreviewsMap: any = {};
          
          this.messages.forEach((message: Message) => {
            const permanentKey = `${message.message}_${message.created_at}`;
            if (previewsMap[permanentKey]) {
              message.file = previewsMap[permanentKey];
              updatedPreviewsMap[permanentKey] = previewsMap[permanentKey];
            } else {
              Object.keys(previewsMap).forEach((key) => {
                if (key.startsWith('temp_') && key.includes(message.message)) {
                  message.file = previewsMap[key];
                  updatedPreviewsMap[permanentKey] = previewsMap[key];
                } else if (!key.startsWith('temp_')) {
                  updatedPreviewsMap[key] = previewsMap[key];
                }
              });
            }
          });
          
          sessionStorage.setItem(storageKey, JSON.stringify(updatedPreviewsMap));
        }
        
        this.scrollToBottom();
      } catch (error) {
        console.error("Error fetching history:", error);
      }
    },
    async sendMessage(customMessage?: string, fileInfo?: any) {
      // Handle Initialize chatbot case
      if (customMessage && customMessage == "Initialize chatbot") {
        await axios.post(`${API_BASE_URL}/chat-message/`, {
          session_id: this.sessionId,
          message: customMessage,
        });
        this.fetchHistory();
        return;
      }

      // Handle clickable messages and user input
      let messageToSend = customMessage || this.userInput;
      if (!messageToSend) return;

      // Clear user input if it was typed
      if (!customMessage) {
        this.userInput = "";
      }

      // Ensure session is initialized
      if (!this.sessionId) {
        this.initializeSession();
      }

      // Add user message to the list with file info if available
      const newMessage: Message = {
        id: Date.now(),
        is_user: true,
        message: messageToSend,
      };
      
      if (fileInfo) {
        newMessage.file = fileInfo;
      }

      this.messages.push(newMessage);

      this.scrollToBottom();
      this.isLoading = true;

      try {
        const formData = new FormData();
        formData.append("session_id", this.sessionId!);
        formData.append("message", messageToSend);

        if (this.selectedFile) {
          formData.append("file", this.selectedFile);
          console.log("File being sent:", this.selectedFile);
        }

        const response = await axios.post(`${API_BASE_URL}/chat-message/`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.data && response.data.data) {
            const botMessage = response.data.data.message;
            if (botMessage.includes("Question") || botMessage.includes("Thank you")) {
                this.updateLocalStorageAnswers(messageToSend);
            }
        }
        
        if (fileInfo) {
          this.storeFilePreview(messageToSend, fileInfo);
        }
        
        await this.fetchHistory();
        
        this.checkForResumeQuestionsCompletion();
      } catch (error) {
        console.error("Error sending message:", error);
      } finally {
        this.isLoading = false;
        this.selectedFile = null;
      }
    },
    storeFilePreview(message: string, fileInfo: any) {
      const storageKey = `file_previews_${this.sessionId}`;
      const storedPreviews = sessionStorage.getItem(storageKey);
      const previewsMap = storedPreviews ? JSON.parse(storedPreviews) : {};
      
      const tempKey = `temp_${message}_${Date.now()}`;
      previewsMap[tempKey] = fileInfo;
      
      sessionStorage.setItem(storageKey, JSON.stringify(previewsMap));
    },
    formatTime(dateString: string | undefined): string {
      if (!dateString) return "";

      return moment(dateString).format("h:mm A");
    },
    scrollToBottom() {
      nextTick(() => {
        if (this.simpleBarInstance) {
          const scrollElement = this.simpleBarInstance.getScrollElement();
          scrollElement.scrollTop = scrollElement.scrollHeight;
        }
      });
    },
    formatMessage(message: string): string {
      return message.replace(/\n/g, "<br>");
    },
    triggerFileUpload() {
      (this.$refs.fileInput as HTMLInputElement).click();
    },
    async handleFileUpload(event: Event) {
      const target = event.target as HTMLInputElement;
      this.selectedFile = target.files?.[0] || null;

      if (!this.selectedFile) return;

      const lastMessage = this.messages[this.messages.length - 1];
      if (lastMessage && !lastMessage.is_user) {
        const messageText = lastMessage.message.toLowerCase();
        
        const fileUrl = await this.convertFileToDataURL(this.selectedFile);
        const fileInfo = {
          name: this.selectedFile.name,
          type: this.selectedFile.type,
          url: fileUrl,
        };

        if (messageText.includes("resume")) {
          await this.sendMessage("resume uploaded", fileInfo);
        } else if (messageText.includes("picture") || messageText.includes("image") || messageText.includes("photo") || messageText.includes("profile")) {
          await this.sendMessage("picture uploaded", fileInfo);
        } else {
          this.selectedFile = null;
          target.value = "";
          return;
        }
      } else {
        this.selectedFile = null;
        target.value = "";
        return;
      }
      // Clear the input
      target.value = "";
    },
    convertFileToDataURL(file: File): Promise<string> {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve(e.target?.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    closeChatbotPopup() {
      const chatbotElement = document.querySelector('.ai-chat-bot');
      if (chatbotElement) {
        chatbotElement.classList.remove('show-chat-bot', 'chat-bot-zoomed');
      }
    },
    openJobFormModal() {
      const modalElement = document.getElementById('joinUs');
      if (modalElement) {
        const modal = new Modal(modalElement);
        modal.show();
      }
    },
    checkForResumeQuestionsCompletion() {
      if (this.messages.length > 0) {
        const lastMessage = this.messages[this.messages.length - 1];
        if (!lastMessage.is_user && lastMessage.message.includes("Thank you for providing all the answers")) {
          setTimeout(() => {
            this.closeChatbotPopup();
            
            const currentPath = window.location.pathname;
            if (currentPath.includes('/our-team') || currentPath.includes('/readMoreAboutJob')) {
              setTimeout(() => {
                this.openJobFormModal();
              }, 500);
            }
          }, 2000);
        }
      }
    },
    updateLocalStorageAnswers(answer: string) {
      try {
          const savedData = localStorage.getItem('jobApplication_temp');
          if (savedData) {
              const data = JSON.parse(savedData);
              
              // Find the current question index from the last bot message
              const lastBotMessage = this.messages
                  .filter(m => !m.is_user)
                  .pop();
              
              if (lastBotMessage) {
                  const questionMatch = lastBotMessage.message.match(/Question (\d+) of/);
                  if (questionMatch) {
                      const questionIndex = parseInt(questionMatch[1]) - 1;
                      
                      // Update the answer
                      if (!data.missingDataAnswers) {
                          data.missingDataAnswers = {};
                      }
                      data.missingDataAnswers[questionIndex] = answer;
                      
                      // Save back to localStorage
                      localStorage.setItem('jobApplication_temp', JSON.stringify(data));
                      console.log('Updated localStorage with answer:', answer);
                  }
              }
          }
      } catch (error) {
          console.error('Error updating localStorage:', error);
      }
  },
  },
});
</script>

<style scoped>
.message-file-preview {
  margin-bottom: 10px;
}

.uploaded-image-preview {
  max-width: 300px;
  max-height: 300px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.uploaded-pdf-preview,
.uploaded-document-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  max-width: 300px;
}

.pdf-icon,
.doc-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.pdf-info,
.doc-info {
  flex: 1;
  overflow: hidden;
}

.pdf-filename,
.doc-filename {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
